import React, { lazy, Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import { ListItem, useOverlay, Tag, useMenuOverlay } from '@fs/zion-ui'
import { MenuMore } from '@fs/zion-icon'
import { getSessionId } from '@fs/zion-session'
import { ContactOverlay } from '@fs/zion-contact-overlay'
import { useGroupsContext } from '@fs/zion-tree-groups'
import { useFeatureFlag } from '@fs/zion-flags'
import { useMembersContext } from '../../providers/MembersProvider'
import MemberMenuMoreOverlay from './MemberMenuMoreOverlay'
import { useUserIsDataAdmin } from '../../providers/DataAdminProvider'

const RemoveMemberOverlay = lazy(() => import('./RemoveMemberOverlay'))
const RemoveSelfAsAdminOverlay = lazy(() => import('./assign-remove-admin/RemoveSelfAsAdminOverlay'))

export default function MemberItem({ member: memberItem, adminCount, hideAdminTag, group }) {
  const [t] = useTranslation()
  const removeMemberOverlay = useOverlay()
  const contactOverlay = useOverlay()
  const menuOverlay = useMenuOverlay()
  const removeAdminOverlay = useOverlay()
  const { treeDetails } = useGroupsContext()
  const { userIsAdmin, currentMemberDetails, refetchGroupMembers } = useMembersContext()
  const { userIsDataAdmin } = useUserIsDataAdmin()
  const newSettingsPageOn = useFeatureFlag('treeWeb_groupManagement_newSettingsPage').isOn

  const isResearcherTree = group.types.includes('RESEARCHER_TREE')

  const userIsOwner = !!treeDetails && currentMemberDetails?.memberCisId === treeDetails.ownerCisId

  const memberItemIsAdmin = memberItem.status === 'ADMIN'
  const memberItemIsOwner = !!treeDetails && memberItem.memberCisId === treeDetails.ownerCisId
  const memberItemIsUser = memberItem.memberCisId === currentMemberDetails?.memberCisId

  const canRemoveMember =
    ((userIsAdmin && !memberItemIsUser && currentMemberDetails?.memberCisId && refetchGroupMembers) ||
      userIsDataAdmin) &&
    /* For now we are not going to allow Data admins to remove the owner of a CET. That could change in the future. */
    (isResearcherTree ? !memberItemIsOwner : true)

  const canAssignAdmin = (isResearcherTree ? userIsOwner || userIsDataAdmin : userIsAdmin) && !memberItemIsAdmin

  const canRemoveAdmin =
    (isResearcherTree ? (userIsOwner || memberItemIsUser || userIsDataAdmin) && !memberItemIsOwner : userIsAdmin) &&
    memberItemIsAdmin

  const canShowLeaveGroup = memberItemIsUser && (isResearcherTree ? !memberItemIsOwner : true)

  let tagTranslationKey = 'member-list.label.administrator'
  if (isResearcherTree) {
    if (memberItemIsOwner) {
      tagTranslationKey = 'group-management.owner.label'
    } else {
      tagTranslationKey = 'group-management.label.co-owner'
    }
  }

  return (
    <>
      <ListItem
        data-testid={`member-item: ${memberItem.userProfileName}`}
        dense={newSettingsPageOn}
        startElement={
          <ListItem.Avatar
            monogram={memberItem.userProfileName?.[0] ?? t('member.name-unknown')[0]}
            // Need to pass the sessionId for localhost to get image
            src={`${memberItem.portraitUrl}?sessionId=${getSessionId()}`}
          />
        }
        onClick={contactOverlay.handleClick}
        primaryText={memberItem.userProfileName ?? t('member.name-unknown')}
        secondaryText={!hideAdminTag && memberItemIsAdmin ? <Tag text={t(tagTranslationKey)} /> : undefined}
        endElement={
          (canAssignAdmin || canRemoveMember || canShowLeaveGroup || canRemoveAdmin) && (
            <ListItem.IconButton
              data-testid={`member-list.menu-more-button-${memberItem.memberCisId}`}
              Icon={MenuMore}
              onClick={menuOverlay.handleClick}
              title={t('group-management.open-menu.action')}
            />
          )
        }
      />

      <MemberMenuMoreOverlay
        {...{
          menuOverlay,
          canAssignAdmin,
          adminCount,
          memberItem,
          isResearcherTree,
          canRemoveAdmin,
          memberItemIsUser,
          removeAdminOverlay,
          canRemoveMember,
          removeMemberOverlay,
          canShowLeaveGroup,
          group,
        }}
      />

      {(contactOverlay.isOpen || contactOverlay.transitioning) && (
        <ContactOverlay contact={{ cisId: memberItem.memberCisId }} overlay={contactOverlay} />
      )}
      {canRemoveMember && (removeMemberOverlay.isOpen || removeMemberOverlay.transitioning) && (
        <Suspense fallback="">
          <RemoveMemberOverlay
            group={group}
            member={memberItem}
            overlay={removeMemberOverlay}
            adminCount={adminCount}
          />
        </Suspense>
      )}

      {canRemoveAdmin && (removeAdminOverlay.isOpen || removeAdminOverlay.transitioning) && (
        <Suspense fallback="">
          <RemoveSelfAsAdminOverlay
            overlay={removeAdminOverlay}
            groupId={group.id}
            memberCisId={memberItem.memberCisId}
            onRemoveSelfAsAdmin={refetchGroupMembers}
            group={group}
          />
        </Suspense>
      )}
    </>
  )
}
